/**
 * TEAM (pure): the crew call sheet — every person on the shoot (with the app or not) with their job,
 * the cameras they run, the place they are posted, notes, where they are ("at CAM SITE" / "1.2 mi NW
 * of you") and whether their position is live; then cameras nobody runs (static, aerial).
 *
 * Job (what they do on the shoot) and app access (what they may change in Airview Studio) are separate:
 * rows carry both, the UI shows the job and keeps app access in the person's details.
 *
 * Assignments drive positions: someone with the app and no live fix shows at the place they are
 * posted; someone without the app shows at their camera, else their place.
 */
import {
  bearingDeg, cameraPerson, crewPeople, CREW_JOB_LABEL, distanceM, lensText, type Camera, type CameraMount, type CrewJob, type CrewPerson, type Id, type LatLon,
  type MissionBundle, type Role, type TargetRef, type WorldState,
} from '@apos/shared';

export type TeamStatus = 'live' | 'aging' | 'stale' | 'waiting' | 'not_sharing' | 'no_app' | 'placed' | 'no_position';

export interface TeamRow {
  key: string;
  kind: 'person' | 'camera';
  name: string;
  /** "Producer · CAM A" / "CAM B · no app" / "Static camera · 50 mm". */
  detail: string;
  you: boolean;
  hasApp: boolean;
  color: string | null;
  /** "at CAM SITE" / "1.2 mi NW of you" / null. */
  where: string | null;
  status: TeamStatus;
  /** "Live" / "Updated 40 s ago" / "Last seen 6 min ago" / "Not sharing" / "Placed". */
  statusText: string;
  position: LatLon | null;
  /** What tapping selects on the map. */
  target: TargetRef | null;
  /** true = has the aircraft in frame (coverage hook), null = unknown / not computed. */
  inFrame: boolean | null;
  // ── crew (people rows; null / empty on camera rows) ──
  personId: Id | null;
  /** A stored person (false for camera rows and people synthesised from older data). */
  editable: boolean;
  job: CrewJob | null;
  /** App access (members only) — not the job. */
  access: Role | null;
  userId: Id | null;
  cameras: Array<{ id: Id; label: string; lens: string | null; mount: CameraMount }>;
  placeId: Id | null;
  placeName: string | null;
  phone: string | null;
  notes: string;
}

type CameraExt = Camera & { operatorName?: string | null; locationId?: Id | null; personId?: Id | null };
const MOUNT_TEXT: Record<CameraMount, string> = { crew: 'Manned', static: 'Static', manual: 'Static', aircraft: 'Aerial' };

/** A place counts as "at" within this distance (or its own radius). */
export const AT_PLACE_M = 150;

const COMPASS8 = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
export function compass8(deg: number): string {
  return COMPASS8[Math.round((((deg % 360) + 360) % 360) / 45) % 8]!;
}

/** Statute distance for the crew: "300 ft" under 0.1 mi, else "1.2 mi" / "14 mi". */
export function crewDistance(m: number): string {
  const mi = m / 1609.344;
  if (mi < 0.1) return `${Math.max(10, Math.round(m / 0.3048 / 10) * 10)} ft`;
  return mi < 9.95 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
}

/** "at CAM SITE" when on a place, else "1.2 mi NW of you" (or of the nearest place when I have no position). */
export function whereText(p: LatLon | null, bundle: Pick<MissionBundle, 'locations'>, myPos: LatLon | null, isMe: boolean): string | null {
  if (!p) return null;
  let nearest: { name: string; d: number; pos: LatLon } | null = null;
  for (const l of bundle.locations) {
    const pos = { lat: l.lat, lon: l.lon };
    const d = distanceM(p, pos);
    if (d <= Math.max(AT_PLACE_M, l.radiusM ?? 0)) return `at ${l.name}`;
    if (!nearest || d < nearest.d) nearest = { name: l.name, d, pos };
  }
  if (isMe) return null;
  if (myPos) {
    const d = distanceM(myPos, p);
    if (d < 30) return 'next to you';
    return `${crewDistance(d)} ${compass8(bearingDeg(myPos, p))} of you`;
  }
  if (nearest && nearest.d < 20_000) return `${crewDistance(nearest.d)} ${compass8(bearingDeg(nearest.pos, p))} of ${nearest.name}`;
  return null;
}

function ageText(sec: number): string {
  return sec < 90 ? `${Math.round(sec)} s` : `${Math.round(sec / 60)} min`;
}

export interface TeamOptions {
  myPos?: LatLon | null;
  /** Coverage hook (lane COVERAGE): does this camera have the focused aircraft in frame? */
  inFrame?: (cameraId: Id) => boolean | null;
}

export function buildTeam(bundle: MissionBundle, world: WorldState | null, opts: TeamOptions = {}): TeamRow[] {
  const me = bundle.me.userId;
  const cams = bundle.cameras as CameraExt[];
  const people: CrewPerson[] = crewPeople(bundle);
  const placeOf = (id: Id | null | undefined) => (id ? (bundle.locations.find((l) => l.id === id) ?? null) : null);
  const camPos = (c: CameraExt): LatLon | null => {
    const st = world?.cameras.find((x) => x.cameraId === c.id);
    if (st?.position) return st.position;
    if (c.position) return c.position;
    const l = placeOf(c.locationId);
    return l ? { lat: l.lat, lon: l.lon } : null;
  };
  const crewPos = (id: Id): LatLon | null => {
    const c = world?.crew.find((x) => x.userId === id);
    return c && c.freshness !== 'none' ? { lat: c.lat, lon: c.lon } : null;
  };
  const myPos = opts.myPos ?? crewPos(me);
  const inFrame = (ids: Id[]): boolean | null => {
    if (!opts.inFrame || ids.length === 0) return null;
    let any: boolean | null = null;
    for (const id of ids) {
      const v = opts.inFrame(id);
      if (v === true) return true;
      if (v === false) any = false;
    }
    return any;
  };
  const owner = new Map<Id, Id | null>(cams.map((c) => [c.id, cameraPerson(c, people)?.id ?? null]));
  const camsOf = (personId: Id) => cams.filter((c) => owner.get(c.id) === personId);
  const camInfo = (c: CameraExt) => ({ id: c.id, label: c.label, lens: lensText(c), mount: c.mount });

  const rows: TeamRow[] = [];
  for (const p of people) {
    const m = p.userId ? bundle.members.find((x) => x.userId === p.userId) : undefined;
    if (p.userId && !m) continue; // a stale link: the member left
    const mine = camsOf(p.id);
    const place = placeOf(p.locationId);
    const placePos = place ? { lat: place.lat, lon: place.lon } : null;
    const you = !!m && m.userId === me;
    const base = {
      key: `person:${p.id}`,
      kind: 'person' as const,
      you,
      personId: p.id,
      editable: !p.virtual,
      job: p.job,
      access: m?.role ?? null,
      userId: p.userId,
      cameras: mine.map(camInfo),
      placeId: place?.id ?? null,
      placeName: place?.name ?? null,
      phone: p.phone,
      notes: p.notes,
      inFrame: inFrame(mine.map((c) => c.id)),
      detail: [p.job ? CREW_JOB_LABEL[p.job] : null, ...mine.map((c) => c.label)].filter(Boolean).join(' · '),
    };
    if (m) {
      const st = world?.crew.find((x) => x.userId === m.userId);
      const live = st && st.freshness !== 'none' ? { lat: st.lat, lon: st.lon } : null;
      let status: TeamStatus;
      let statusText: string;
      if (!st || st.freshness === 'none') {
        status = m.sharingLocation ? 'waiting' : 'not_sharing';
        statusText = m.sharingLocation ? 'Waiting for location' : 'Not sharing';
      } else if (st.freshness === 'live') {
        status = 'live';
        statusText = 'Live';
      } else if (st.freshness === 'aging') {
        status = 'aging';
        statusText = `Updated ${ageText(st.ageSec)} ago`;
      } else {
        status = 'stale';
        statusText = `Last seen ${ageText(st.ageSec)} ago`;
      }
      // no live fix → where they are posted
      const position = live ?? placePos;
      rows.push({
        ...base,
        name: m.displayName || m.callsign,
        hasApp: true,
        color: m.color,
        where: live ? whereText(live, bundle, you ? null : myPos, you) : place ? `posted at ${place.name}` : null,
        status,
        statusText,
        position,
        target: { kind: 'crew', id: m.userId },
      });
    } else {
      // without the app: at their camera, else at their place
      const cam = mine.find((c) => camPos(c));
      const position = (cam ? camPos(cam) : null) ?? placePos;
      rows.push({
        ...base,
        name: p.name,
        hasApp: false,
        color: null,
        where: position ? whereText(position, bundle, myPos, false) : null,
        status: 'no_app',
        statusText: 'No app',
        position,
        target: cam ? { kind: 'camera', id: cam.id } : null,
      });
    }
  }

  // cameras nobody runs: a static camera, an aerial camera
  for (const c of cams) {
    if (owner.get(c.id)) continue;
    const pos = camPos(c);
    const ac = c.mount === 'aircraft' && c.aircraftId ? bundle.aircraft.find((a) => a.id === c.aircraftId) : undefined;
    const lens = lensText(c);
    const detail = ac ? `Aerial · on ${ac.label}` : [`${MOUNT_TEXT[c.mount]} camera`, lens].filter(Boolean).join(' · ');
    const st = world?.cameras.find((x) => x.cameraId === c.id);
    const live = c.mount === 'aircraft' && st?.position && (st.freshness === 'live' || st.freshness === 'aging');
    rows.push({
      key: `camera:${c.id}`,
      kind: 'camera',
      name: c.label,
      detail,
      you: false,
      hasApp: false,
      color: null,
      where: whereText(pos, bundle, myPos, false),
      status: live ? 'live' : pos ? 'placed' : 'no_position',
      statusText: live ? 'Live (aircraft)' : pos ? 'Placed' : 'No position set',
      position: pos,
      target: { kind: 'camera', id: c.id },
      inFrame: inFrame([c.id]),
      personId: null,
      editable: false,
      job: null,
      access: null,
      userId: null,
      cameras: [camInfo(c)],
      placeId: c.locationId ?? null,
      placeName: placeOf(c.locationId)?.name ?? null,
      phone: null,
      notes: '',
    });
  }

  const rank = (r: TeamRow) => (r.you ? 0 : r.hasApp ? 1 : r.kind === 'person' ? 2 : 3);
  return rows.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** "4 people · 2 with the app live · 3 cameras" for the header. */
export function teamSummary(rows: readonly TeamRow[], bundle: Pick<MissionBundle, 'cameras'>): string {
  const people = rows.filter((r) => r.kind === 'person').length;
  const live = rows.filter((r) => r.hasApp && (r.status === 'live' || r.status === 'aging')).length;
  const cams = bundle.cameras.length;
  const parts = [`${people} ${people === 1 ? 'person' : 'people'}`, `${live} live`];
  if (cams) parts.push(`${cams} ${cams === 1 ? 'camera' : 'cameras'}`);
  return parts.join(' · ');
}

